<template>
  <div class="mainMenu" :class="`locale-${currentLocale}`">
    <div class="menuLogoWrap" :class="{active: !menuStore.isCollapse}">
      <div class="menuLogoPanel">
        <img :src="menuLogo" alt="" class="menuLogo">
        <div class="projectName" :class="{show: !menuStore.isCollapse}">{{ $t('header.projectName') }}</div>
      </div>
    </div>
    <el-menu :default-active="defaultActive" :class="{ menuCollapse: menuStore.isCollapse }" unique-opened>
      <template v-for="item in menuFmt" :key="item.id">
        <el-sub-menu v-if="Array.isArray(item.children)" :index="item.id" :class="{subMenuActive:subMenuActive(item)}">
          <template #title  >
            <img v-show="item.icon"
                 class="menuIcon"
                 :src="subMenuActive(item)? item.activeIcon: item.icon"
                 alt="menuIcon">
            <span v-show="!menuStore.isCollapse" class="subMenuName">{{ item.name }}</span>
          </template>
          <!--          只做两层，菜单嵌套层数过多体验不好， 如有这样的需求产品应重新考虑展现形式， 例如头部菜单等-->
          <el-menu-item v-show="!menuStore.isCollapse" v-for="subItem in item.children" :index="subItem.id"
                        :key="subItem.id"
                        :class="{activeMenu: subItem.id === activeMenu.id}"
                        @click="onMenuClick(subItem, subItem.blank || false)">
            <template #title>
              <img v-show="subItem.icon" class="menuIcon subMenuIcon"
                   :src="subItem.id === activeMenu.id? subItem.activeIcon: subItem.icon"
                   alt="menuIcon">
              <span class="subMenuTitle">{{ subItem.name }}</span>
              <div v-show="subItem.id === activeMenu.id" class="activeMenuBarWrap">
                <span class="activeMenuBar"></span>
              </div>
            </template>
          </el-menu-item>
        </el-sub-menu>
        <el-menu-item v-else :index="item.id" @click="onMenuClick(item, item.blank || false)"
                      :class="{activeMenu: item.id === activeMenu.id}">
          <template #title>
            <img
                v-show="item.icon"
                class="menuIcon"
                :src="item.id === activeMenu.id? item.activeIcon: item.icon"
                alt="menuIcon">
            <span v-show="!menuStore.isCollapse" class="boldFontMenu">{{ item.name }}</span>
            <div v-show="item.id === activeMenu.id" class="activeMenuBarWrap">
              <span class="activeMenuBar"></span>
            </div>
          </template>
        </el-menu-item>
      </template>
    </el-menu>
  </div>
</template>

<script lang="ts" setup>
  import {menu as menuDefault, MenuItemInterface} from './menu';
  import {menu as menuPinia} from '../../pinia/menu';
  import {findMenuByPath} from './layout-menu';
  import {authClassify} from '@/common/utils/utils';
  import { ElMessage } from 'element-plus';
  import { useI18n } from 'vue-i18n';

  const { t, locale } = useI18n();
  const currentLocale = computed(() => locale.value);

  import serveGrayIcon from './img/serve-blue.png';
  import serveBlueIcon from './img/serve-deep-blue.png';
  import openGrayIcon from './img/open-blue.png';
  import openBlueIcon from './img/open-deep-blue.png';
  import deviceGrayIcon from './img/device-blue.png';
  import deviceBlueIcon from './img/device-deep-blue.png';
  import customGrayIcon from './img/custom-blue.png';
  import customBlueIcon from './img/custom-deep-blue.png';
  import employeeGrayIcon from './img/employee-blue.png';
  import employeeBlueIcon from './img/employee-deep-blue.png';
  import systemGrayIcon from './img/system-blue.png';
  import systemBlueIcon from './img/system-deep-blue.png';

  import menuLogo from '@vImg/logo.png';

  const router = useRouter();
  const route = useRoute();

  const defaultActive = ref('');
  const initMenuList = ref<any[]>([])
  // 保存完整的原始后端数据（包含多语言字段），用于语言切换时重新处理
  const rawMenuData = ref<any[]>([])
  const activeMenu = reactive<MenuItemInterface>({
    id: '',
    path: '',
    name: ''
  });

  const menuStore = menuPinia();
  const {isCollapse} = storeToRefs(menuStore);

  // 后端返回但凡有点变动，都会出问题
  const iconlist = [{
    icon: serveGrayIcon,
    activeIcon: serveBlueIcon
  }, {
    icon: openGrayIcon,
    activeIcon: openBlueIcon
  }, {
    icon: deviceGrayIcon,
    activeIcon: deviceBlueIcon
  }, {
    icon: customGrayIcon,
    activeIcon: customBlueIcon
  }, {
    icon: employeeGrayIcon,
    activeIcon: employeeBlueIcon
  }, {
    icon: systemGrayIcon,
    activeIcon: systemBlueIcon
  }]


  const iconMap = {
    "服务中心": {
      icon: serveGrayIcon,
      activeIcon: serveBlueIcon
    },
    "设备中心": {
      icon: deviceGrayIcon,
      activeIcon: deviceBlueIcon
    },
    "开放平台": {
      icon: openGrayIcon,
      activeIcon: openBlueIcon
    },
    "我的客户": {
      icon: customGrayIcon,
      activeIcon: customBlueIcon
    },
    "系统管理": {
      icon: employeeGrayIcon,
      activeIcon: employeeBlueIcon
    },
    "用户中心": {
      icon: systemGrayIcon,
      activeIcon: systemBlueIcon
    },
  }

  const menuFmt = computed(()=>{
    return menuStore.menuList
  });

  // 语言代码映射：项目语言代码 -> 后端语言代码
  const mapLocaleToBackend = (loc: string): string => {
    const localeMap: Record<string, string> = {
      'zh-cn': 'zh-CN',
      'zh-tw': 'zh-TW',
      'en': 'en'
    };
    return localeMap[loc] || 'zh-CN';
  };

  // 根据当前语言获取多语言文本，带兜底逻辑
  const getLocalizedText = (languageObj: any, fallback: string): string => {
    if (!languageObj || typeof languageObj !== 'object') {
      return fallback || '';
    }
    const backendLocale = mapLocaleToBackend(locale.value);
    const text = languageObj[backendLocale];
    // 如果对应语言不存在，使用兜底文本
    return text || fallback || '';
  };

  // 根据当前语言处理菜单数据
  const processMenuWithLocale = (rawData: any[], currentLocale: string): any[] => {
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return [];
    }

    // 先获取所有启用的菜单项，并保留原始数据
    const templist = rawData.filter((ele: any) => ele.enable);

    // 处理每个菜单项，使用多语言字段
    const processedItems = templist.map((ele: any) => {
      // 处理页面名称：优先使用 pageNameLanguage，兜底使用 pageName
      const pageName = getLocalizedText(ele.pageNameLanguage, ele.pageName);
      // 处理菜单名称：优先使用 menuNameLanguage，兜底使用 menuName
      const menuName = getLocalizedText(ele.menuNameLanguage, ele.menuName);

      return {
        ...ele,
        name: pageName, // 用于二级菜单显示
        menuName: menuName, // 保留用于一级菜单分组
        id: ele.permissionCode
      };
    }).filter((ii: any) => {
      return (!ii.position || ii.position === 'left') && !ii.controlPermission
    });

    // 使用 authClassify 进行分类（按 menuName 分组）
    const menuList = authClassify(processedItems, currentLocale);

    // 特殊处理：path === '/about' 的项作为一级菜单单独展示（无子菜单）
    const STANDALONE_ABOUT_PATH = '/about';
    const DEFAULT_SORT = 999999;

    const standaloneAboutItems: any[] = [];
    const groupsWithFilteredChildren = menuList
      .map((group: any) => {
        const aboutChildren = (group.children || []).filter((c: any) => c.path === STANDALONE_ABOUT_PATH);
        const restChildren = (group.children || []).filter((c: any) => c.path !== STANDALONE_ABOUT_PATH);

        aboutChildren.forEach((c: any) => standaloneAboutItems.push(c));

        if (restChildren.length === 0) return null;
        return { ...group, children: restChildren };
      })
      .filter(Boolean);

    // 将 standalone 项转为一级菜单格式（无 children）
    const standaloneAsTopLevel = standaloneAboutItems.map((item: any) => ({
      ...item,
      id: item.id,
      name: item.name,
      path: item.path,
      icon: '',
      activeIcon: '',
      children: undefined,
      _sort: item.sort !== undefined && item.sort !== null ? item.sort : DEFAULT_SORT
    }));

    // 合并：分组 +  standalone 项，按 sort 排序
    const getSortForItem = (item: any) => {
      if (item._sort !== undefined) return item._sort;
      const children = item.children || [];
      if (children.length === 0) return DEFAULT_SORT;
      return Math.min(...children.map((c: any) => (c.sort !== undefined && c.sort !== null ? c.sort : DEFAULT_SORT)));
    };
    const mergedList = [...groupsWithFilteredChildren, ...standaloneAsTopLevel].sort(
      (a, b) => getSortForItem(a) - getSortForItem(b)
    );
    mergedList.forEach((item: any) => delete item._sort);

    // 过滤并处理图标
    return mergedList.filter((k: any) => {
      return (!k.position || k.position === 'left') && !k.controlPermission;
    }).map((ele: any, i: number) => {
      // 处理一级菜单名称：ele.vIdName 是多语言版本的 menuName（来自 authClassify）
      // 需要找到该分组下的第一个原始数据，获取其 menuNameLanguage
      // 注意：ele.vIdName 是处理后的多语言名称，需要通过原始 menuName 匹配
      const firstProcessedItem = processedItems.find((item: any) => item.menuName === ele.vIdName);
      const firstRawItem = firstProcessedItem
        ? rawData.find((item: any) => item.permissionCode === firstProcessedItem.id)
        : null;

      // 获取原始 menuName（用于查找图标，因为 iconMap 的 key 是原始中文名称）
      const originalMenuName = firstRawItem ? firstRawItem.menuName : ele.vIdName;

      // 获取多语言菜单名称（用于显示）
      const menuName = firstRawItem
        ? getLocalizedText(firstRawItem.menuNameLanguage, firstRawItem.menuName || ele.vIdName)
        : ele.vIdName || ele.name;

      // 处理二级菜单：确保使用多语言文本
      const children = (ele.children || []).map((child: any) => {
        // 找到对应的原始数据
        const rawItem = rawData.find((item: any) => item.permissionCode === child.id);
        const childPageName = rawItem
          ? getLocalizedText(rawItem.pageNameLanguage, rawItem.pageName || child.name)
          : child.name;

        return {
          ...child,
          name: childPageName
        };
      });

      // 使用原始 menuName 查找图标（因为 iconMap 的 key 是原始中文名称）
      // @ts-ignore
      const icon = iconMap[originalMenuName] ? iconMap[originalMenuName].icon : "";
      // @ts-ignore
      const activeIcon = iconMap[originalMenuName] ? iconMap[originalMenuName].activeIcon : icon;

      return {
        ...ele,
        name: menuName, // 一级菜单名称（已处理多语言）
        icon: icon,
        activeIcon: activeIcon,
        // standalone 的「关于我们」保持 children 为 undefined，渲染为 el-menu-item
        children: ele.path === STANDALONE_ABOUT_PATH ? undefined : children
      };
    });
  };

  // 获取权限数据
  const getRoleAuthList = async () => {
    const roleId = aStore.get('userInfo').roleId
    const { success, fail } = await aServe.getRoleAuthList({ roleId });
    if (Array.isArray(success)) {
      // 保存完整的原始后端数据（包含多语言字段）
      rawMenuData.value = [...success];

      menuStore.updateAuthList([...success])

      // 根据当前语言处理菜单数据
      const processedMenuList = processMenuWithLocale(success, locale.value);

      return processedMenuList;
    }
    if (fail) {
      ElMessage({
        message: "请求失败",
        type: "error",
      });
    }
  };

  onMounted(async () => {
    const menuList = await getRoleAuthList()
    if (menuList) {
      initMenuList.value = menuList;
      menuUpdate(initMenuList.value);
      menuStore.updateMenuList(initMenuList.value)
    }
  });

  const onMenuClick = (data: MenuItemInterface, blank: boolean) => {
    if (data) {
      activeMenu.id = data.id;
      activeMenu.name = data.name;
      activeMenu.path = data.path;
    }
    if (!blank) {
      data.path && router.push({
        path: data.path
      });
    } else {
      if (data.path) {
        const href = router.resolve({
          path: data.path
        });
        window.open(href.href, '_blank');
      }
    }
  };

  const subMenuActive = (item: MenuItemInterface) => {
    const children = item.children || [];
    return !!children.find(k => {
      return k.id === activeMenu.id;
    });
  };

  watch(() => {
    return route.path;
  }, () => {
    menuUpdate(initMenuList.value)
  });

  // 监听语言变化，重新处理菜单数据
  watch(() => locale.value, async () => {
    if (rawMenuData.value && rawMenuData.value.length > 0) {
      // 根据新语言重新处理菜单数据
      const processedMenuList = processMenuWithLocale(rawMenuData.value, locale.value);
      initMenuList.value = processedMenuList;
      // 更新 store，面包屑会自动使用新的多语言名称
      menuStore.updateMenuList(processedMenuList);
      // 更新当前菜单状态
      menuUpdate(processedMenuList);
    }
  });

  const menuUpdate = (initMenuList: any) => {
    const currentMenu = findMenuByPath(route.path, initMenuList);
    if (currentMenu) {
      activeMenu.id = currentMenu.id;
      activeMenu.name = currentMenu.name;
      activeMenu.path = currentMenu.path;
      defaultActive.value = currentMenu.id;
    }
    menuStore.updateCurrentMenu(route.path);
  };

</script>

<style scoped lang="scss">
  .foldBox {
    position: absolute;
    bottom: 20px;
    left: 20px;
    cursor: pointer;

    .foldIcon {
      font-size: 20px;
    }

  }

  .mainMenu {
    padding-top: 60px;

    .menuLogoWrap {
      position: absolute;
      top: 0;
      left: 0;
      width: 68px;
      z-index: 1;
      background: white;

      .menuLogoPanel {
        box-sizing: border-box;
        width: 68px;
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        font-weight: bold;
        padding: 10px 0 10px 12px;
      }

      .menuLogo {
        cursor: pointer;
        width: 42px;
        margin-right: 2px;
      }

      .projectName {
        opacity: 0;
        font-size: 19px;
        display: none;

        &.show {
          opacity: 1;
          display: block;
        }
      }

      &.active {
        width: 210px;
        .menuLogoPanel {
          width: 210px;
          padding-left: 4px;
        }
      }
    }



    &.locale-en {
      .menuLogoWrap {
        &.active {
          width: 228px;
          .menuLogoPanel {
            width: 228px;
          }
        }
      }
    }

    :deep(.eh-menu) {
      border-right: none;
      .subMenuName, .subMenuTitle {
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
      }
      .subMenuActive {
        .subMenuName {
          color: var(--color-primary);
        }

        .eh-sub-menu__icon-arrow {
          color: var(--color-primary);
        }
      }

      .eh-menu-item,
      .eh-sub-menu__title {
        color: var(--color-text-regular);
        font-size: 16px;
      }
      .eh-sub-menu__title {
        padding-left: 16px;
        height: 66px;
      }
      .eh-menu-item {
        height: 66px;
        padding-left: 54px;
      }

      .eh-menu-item {
        &.is-active {
          background: var(--color-background-hover);
          color: var(--color-primary);
        }

        &:hover {
          background: var(--color-background-hover);
          color: var(--color-primary);
        }
      }

      .menuIcon {
        margin-right: 8px;
        width: 32px;
        line-height: 1;
      }

      .subMenuIcon {
        font-size: 18px;
      }

      .activeMenu {
        position: relative;

        .activeMenuBarWrap {
          position: absolute;
          right: 10px;
          top: 0;
          bottom: 0;
          display: flex;
          align-items: center;
        }

        .activeMenuBar {
          width: 3px;
          height: 14px;
          box-shadow: 0px 0px 0px 2px var(--border-color-primary);
          background: var(--color-primary);
          border-radius: 2px;
        }
      }


      &.menuCollapse {
        .eh-sub-menu__icon-arrow {
          display: none;
        }
      }
    }
  }
  .boldFontMenu {
    font-weight: bold;
  }
</style>
